import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  canAbortTrace,
  canRerunTrace,
  canResumeTrace,
  isArchivedTracePreview,
  toasterApi,
} from '@terra/ui-design-system';
import { takeMutableArchiveSelection } from '@terra/shared-dto-types';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { terraProxyApiClient } from '../clients/testerClient';
import { useExecutionsStore } from '../store/executionsStore';
import {
  chunkThreadIds,
  countResults,
  isNeoOutOfScopeError,
  pluralize,
} from './trace-action-helpers';
import {
  abortTraceExecution,
  rerunTraceExecution,
  resumeTraceExecution,
} from './trace-agent-actions';
import { useNeoOutOfScopeToast } from './useNeoOutOfScopeToast';

type SelectedTraces = Map<string, TracePreviewDto>;

export function useTraceBulkActions({
  applicationId,
  onComplete,
}: {
  applicationId: string | undefined;
  onComplete: () => void;
}) {
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const tExecutions = useTranslations('designSystem.executions');
  const getNeoOutOfScopeToast = useNeoOutOfScopeToast();
  const removeNeoAttackByThreadId = useExecutionsStore(
    (state) => state.removeNeoAttackByThreadId,
  );
  const [isBulkResuming, setIsBulkResuming] = useState(false);
  const [isBulkRerunning, setIsBulkRerunning] = useState(false);
  const [isBulkAborting, setIsBulkAborting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const takeMutableSelectedTraces = useCallback(
    (selectedTraces: SelectedTraces) => {
      const selection = takeMutableArchiveSelection(
        Array.from(selectedTraces.values()),
        isArchivedTracePreview,
      );

      if (selection.status === 'empty') {
        toasterApi.error({
          title: tLifecycle('archivedExecutionActionTooltip'),
        });

        return null;
      }

      return selection.items;
    },
    [tLifecycle],
  );

  const handleBulkResume = useCallback(
    async (selectedTraces: SelectedTraces) => {
      if (!applicationId) return;

      const mutableTraces = takeMutableSelectedTraces(selectedTraces);

      if (!mutableTraces) return;

      const resumable = mutableTraces.filter(canResumeTrace);

      if (resumable.length === 0) return;

      setIsBulkResuming(true);

      try {
        const results = await Promise.allSettled(
          resumable.map((trace) =>
            resumeTraceExecution({ trace, applicationId }),
          ),
        );

        const { succeeded, failed } = countResults(results);

        if (succeeded > 0) {
          toasterApi.success({
            title: `Resumed ${succeeded} ${pluralize(succeeded, 'execution')}`,
            description:
              failed > 0
                ? `${failed} ${pluralize(failed, 'execution')} failed to resume.`
                : 'All selected executions are resuming.',
          });
          onComplete();
        }

        if (failed > 0 && succeeded === 0) {
          toasterApi.error({
            title: 'Failed to resume executions',
            description: `All ${failed} ${pluralize(failed, 'execution')} failed to resume.`,
          });
        }
      } finally {
        setIsBulkResuming(false);
      }
    },
    [applicationId, onComplete, takeMutableSelectedTraces],
  );

  const handleBulkRerun = useCallback(
    async (selectedTraces: SelectedTraces) => {
      if (!applicationId) return;

      const mutableTraces = takeMutableSelectedTraces(selectedTraces);

      if (!mutableTraces) return;

      const rerunable = mutableTraces.filter(canRerunTrace);

      if (rerunable.length === 0) return;

      setIsBulkRerunning(true);

      try {
        const settledResults = await Promise.allSettled(
          rerunable.map((trace) =>
            rerunTraceExecution({ trace, applicationId }),
          ),
        );
        const startedCount = settledResults.filter(
          (result) => result.status === 'fulfilled' && result.value.started,
        ).length;
        const failedCount = settledResults.length - startedCount;
        const outOfScopeCount = settledResults.filter(
          (result) =>
            result.status === 'fulfilled' &&
            isNeoOutOfScopeError(result.value.error),
        ).length;

        if (startedCount > 0) {
          toasterApi.success({
            title: `Started ${startedCount} ${pluralize(startedCount, 'run')}`,
            description:
              failedCount > 0
                ? `${failedCount} ${pluralize(failedCount, 'run')} failed to start.`
                : 'All selected runs are starting.',
          });
          onComplete();
        }

        if (failedCount > 0 && startedCount === 0) {
          toasterApi.error(
            outOfScopeCount === failedCount
              ? getNeoOutOfScopeToast()
              : {
                  title: 'Failed to start runs',
                  description: `All ${failedCount} ${pluralize(failedCount, 'run')} failed to start.`,
                },
          );
        }
      } finally {
        setIsBulkRerunning(false);
      }
    },
    [
      applicationId,
      getNeoOutOfScopeToast,
      onComplete,
      takeMutableSelectedTraces,
    ],
  );

  const handleBulkAbort = useCallback(
    async (selectedTraces: SelectedTraces) => {
      if (!applicationId) return;

      const mutableTraces = takeMutableSelectedTraces(selectedTraces);

      if (!mutableTraces) return;

      const abortable = mutableTraces.filter(canAbortTrace);

      if (abortable.length === 0) return;

      setIsBulkAborting(true);

      try {
        const results = await Promise.allSettled(
          abortable.map((trace) =>
            abortTraceExecution({ trace, applicationId }),
          ),
        );

        const { succeeded, failed } = countResults(results);

        if (succeeded > 0) {
          toasterApi.success({
            title: `Aborted ${succeeded} ${pluralize(succeeded, 'execution')}`,
            description:
              failed > 0
                ? `${failed} ${pluralize(failed, 'execution')} failed to abort.`
                : 'All selected running executions are being stopped.',
          });
          onComplete();
        }

        if (failed > 0 && succeeded === 0) {
          toasterApi.error({
            title: 'Failed to abort executions',
            description: `All ${failed} ${pluralize(failed, 'execution')} failed to abort.`,
          });
        }
      } finally {
        setIsBulkAborting(false);
      }
    },
    [applicationId, onComplete, takeMutableSelectedTraces],
  );

  const handleBulkDelete = useCallback(
    async (selectedTraces: SelectedTraces) => {
      if (!applicationId) return;

      const mutableTraces = takeMutableSelectedTraces(selectedTraces);

      if (!mutableTraces) return;

      setIsBulkDeleting(true);

      try {
        const results = await Promise.allSettled(
          chunkThreadIds(mutableTraces.map((trace) => trace.threadId)).map(
            (threadIds) =>
              terraProxyApiClient.deleteTracesBulk({
                applicationId,
                threadIds,
              }),
          ),
        );
        const deletedThreadIds = results.flatMap((result) =>
          result.status === 'fulfilled' ? result.value.deletedThreadIds : [],
        );
        const failedCount = mutableTraces.length - deletedThreadIds.length;

        deletedThreadIds.forEach(removeNeoAttackByThreadId);

        if (deletedThreadIds.length > 0) {
          if (failedCount === 0) {
            toasterApi.success({
              title: tExecutions('delete.success'),
            });
          }

          onComplete();
        }

        if (failedCount > 0) {
          throw new Error('Failed to delete executions');
        }
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [
      applicationId,
      onComplete,
      removeNeoAttackByThreadId,
      takeMutableSelectedTraces,
      tExecutions,
    ],
  );

  return {
    handleBulkResume,
    handleBulkRerun,
    handleBulkAbort,
    handleBulkDelete,
    isBulkResuming,
    isBulkRerunning,
    isBulkAborting,
    isBulkDeleting,
  };
}
